/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Spin,
  Col,
  Modal,
  DatePicker,
  message,
} from 'antd';
import Table from 'components/Table';
import { CheckCircleOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';
import { getEstadosGlobales } from 'api/catalogos';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import { onError, onSuccess } from 'utils/handlers';
import { formatReceivedValues, formatReceived } from 'utils/formatValues';
import { DATE_FRONTEND_FORMAT } from 'utils/normalizers';
import {
  getLiquidacionesConfiguracionesIncidencias,
  getLiquidacionConfiguracionIncidencia,
  postLiquidacionConfiguracionIncidencia,
  patchLiquidacionConfiguracionIncidencia,
  deleteLiquidacionConfiguracionIncidencia,
} from 'api/nominas/liquidaciones/liquidacionesConfiguracionesIncidencias';
import { getConfiguracionIncidencias } from 'api/nominas/incidencias/configuracionIncidencias';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
  ESTATUS_INACTIVO,
  ESTATUS_RECHAZADO,
} from 'utils/constants';

export const liquidacionConfiguracionIncidenciaPermission = {
  permissionModel: 'liquidacionconfiguracionincidencia',
  catalogsModels: [],
  tabsModels: [],
};

const LiquidacionesConfiguracionesIncidencias = ({ permission, liquidacionConfiguracion }) => {
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [estados, setEstados] = useState([]);
  const [data, setData] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [configuracionIncidencias, setConfiguracionIncidencias] = useState([]);
  const [incidenciaDelete, setIncidenciaDelete] = useState();
  const [incidenciasFiltered, setIncidenciasFiltered] = useState([]);
  const [disableAuth, setDisableAuth] = useState(false);
  const [disableReactivar, setDisableReactivar] = useState(true);
  const [disableCancelar, setDisableCancelar] = useState(false);
  // Un registro Autorizado o Cancelado ya no debe poder editarse ni eliminarse.
  const [disableEditDelete, setDisableEditDelete] = useState(true);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [visibleReactivarConfirmation, setVisibleReactivarConfirmation] = useState(false);
  const [visibleCancelarConfirmation, setVisibleCancelarConfirmation] = useState(false);

  const formInstances = [
    form,
  ];

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getLiquidacionesConfiguracionesIncidencias();
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  const filterIncidencias = (_configuracionIncidencias) => {
    const _incidenciasFiltered = _configuracionIncidencias.filter((inc) => !data.some((val) => val?.incidencia === inc?.id));
    return _incidenciasFiltered;
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _configuracionIncidencias,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getConfiguracionIncidencias({ aplica_incidencia_para_descuento_en_liquidaciones: true, estados_globales: ESTATUS_AUTORIZADO }),
        getLiquidacionesConfiguracionesIncidencias(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setConfiguracionIncidencias(formatReceived(_configuracionIncidencias));
        setData(formatReceivedClone(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setIncidenciasFiltered(filterIncidencias(configuracionIncidencias));
    setSelectedRowKeys([]);
    setVisible(false);
    setDisableAuth(false);
    setDisableReactivar(true);
    setDisableCancelar(false);
    setDisableEditDelete(true);
    form.resetFields();
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteLiquidacionConfiguracionIncidencia(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    if (!match) {
      setLoading(false);
      return;
    }
    // El doble clic en una fila llama a esta funcion directo, sin pasar por el
    // boton "Editar" del toolbar (ver onRow.onDoubleClick en components/Table.jsx).
    if (match.estados_globales === ESTATUS_AUTORIZADO || match.estados_globales === ESTATUS_CANCELADO) {
      setLoading(false);
      message.warn('No se puede editar una Incidencia Autorizada o Cancelada.');
      return;
    }
    const liquidacionConfigIncidencia = await getLiquidacionConfiguracionIncidencia(match.id);
    const configIncidencia = configuracionIncidencias.find((e) => e.id === liquidacionConfigIncidencia?.incidencia);
    const _incidenciasFiltered = filterIncidencias(configuracionIncidencias);
    setTimeout(() => {
      if (liquidacionConfigIncidencia) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue({
            ...formatReceivedClone(liquidacionConfigIncidencia),
            incidencia: configIncidencia?.id || null,
          });
          setIncidenciasFiltered(_incidenciasFiltered.concat(configIncidencia));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchLiquidacionConfiguracionIncidencia(key, values)
        : postLiquidacionConfiguracionIncidencia(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };
  const onClickDelete = () => {
    setIncidenciaDelete(configuracionIncidencias.find((inc) => inc.id === form.getFieldValue('incidencia'))?.concepto);
    setVisibleAlert(true);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar una incidencia ya autorizada');
        return;
      }
      const vals = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchLiquidacionConfiguracionIncidencia(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setConfirmationVisible(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishReactivarConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede Reactivar una Incidencia Activa');
        return;
      }
      const vals = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        estados_globales: ESTATUS_ACTIVO,
        vigencia_final: null,
      };
      const res = await patchLiquidacionConfiguracionIncidencia(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleReactivarConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishCancelarConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede cancelar una incidencia ya cancelada');
        return;
      }
      const vals = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        estados_globales: ESTATUS_CANCELADO,
      };
      const res = await patchLiquidacionConfiguracionIncidencia(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleCancelarConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    const isActivo = record?.estados_globales === ESTATUS_ACTIVO;
    const isCancelado = record?.estados_globales === ESTATUS_CANCELADO;
    const isRechazado = record?.estados_globales === ESTATUS_RECHAZADO;
    setDisableAuth(isAutorizado || isInactivo || isCancelado || isRechazado);
    setDisableReactivar(isActivo);
    setDisableCancelar(isCancelado);
    setDisableEditDelete(isAutorizado || isCancelado);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    estatus: [requiredRule],
  };

  const columns = [
    {
      titleText: 'Incidencia',
      dataIndex: 'incidencia',
      key: 'incidencia',
      width: 500,
      render: (val) => {
        const _configuracionIncidencias = configuracionIncidencias.find((inc) => inc.id === val);
        return _configuracionIncidencias ? `${_configuracionIncidencias?.clave} - ${_configuracionIncidencias?.concepto}` : '';
      },
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 200,
      render: (val) => (val ? val?.format?.(DATE_FRONTEND_FORMAT) ?? val : ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 300,
      render: (val) => estados.find((estado) => estado.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          data={data}
          cols={columns}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setConfirmationVisible(true),
              permission: { model: 'liquidacionconfiguracionincidencia', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length || disableAuth,
            },
            {
              onClick: () => setVisibleReactivarConfirmation(true),
              permission: { model: 'liquidacionconfiguracionincidencia', action: 'reactivar' },
              text: 'Reactivar',
              icon: <RedoOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length || disableReactivar,
            },
            {
              onClick: () => setVisibleCancelarConfirmation(true),
              permission: { model: 'liquidacionconfiguracionincidencia', action: 'cancelar' },
              text: 'Cancelar',
              icon: <CloseCircleOutlined />,
              disabledBySelection: false,
              className: 'cancel',
              disabled: !selectedRowKeys?.length || disableCancelar,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
          disableEdit={!selectedRowKeys?.length || disableEditDelete}
          disableDelete={!selectedRowKeys?.length || disableEditDelete}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          footer={null}
          width={500}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Incidencia`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col span={24}>
              <Form.Item
                name="incidencia"
                label="Incidencia"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={incidenciasFiltered}
                  render={(val) => `${val?.clave} - ${val?.concepto}`}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="vigencia_final"
                label="Vigencia Final"
                hasFeedback
              >
                <DatePicker
                  format={DATE_FRONTEND_FORMAT}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                />
              </Form.Item>
            </Col>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Incidencia ${incidenciaDelete}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => setConfirmationVisible(false)}
          open={confirmationVisible}
          contentTitle={`Autorizar la Incidencia ${configuracionIncidencias.find((inc) => inc.id === form.getFieldValue('incidencia'))?.concepto || ''}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishReactivarConfirmation}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => {
            setVisibleReactivarConfirmation(false);
            onCancel();
          }}
          open={visibleReactivarConfirmation}
          contentTitle={`Reactivar la Incidencia ${configuracionIncidencias.find((inc) => inc.id === form.getFieldValue('incidencia'))?.concepto || ''}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishCancelarConfirmation}
          ConfirmCancel
          onCancel={() => {
            setVisibleCancelarConfirmation(false);
            onCancel();
          }}
          open={visibleCancelarConfirmation}
          contentTitle={`Cancelar la Incidencia ${configuracionIncidencias.find((inc) => inc.id === form.getFieldValue('incidencia'))?.concepto || ''}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default LiquidacionesConfiguracionesIncidencias;
